import { Controller } from "@hotwired/stimulus"
import { applyStyle } from "../builder/style_tokens"
import BuilderApi from "../builder/api"

// Owns the iframe and keeps its DOM in step with the store using the
// cheapest update each change allows:
//   style     -> setProperty, no network, no re-render
//   text      -> already in the DOM, only the store needs updating
//   structure -> insert rendered html / detach / relocate
//   document  -> full re-render through the server (undo only)
export default class extends Controller {
  static targets = ["frame", "wrapper"]

  connect() {
    this.root = this.element.closest("[data-controller~='website-builder']")
    this.frameTarget.addEventListener("load", () => this.bindCanvas())
    // The iframe usually finishes loading before Stimulus connects, in which
    // case the load event above never fires.
    if (this.frameTarget.contentDocument?.readyState === "complete") this.bindCanvas()
    this.root.addEventListener("builder:device", (event) => this.setWidth(event.detail.width))
    this.root.addEventListener("builder:part", (event) => this.loadPart(event.detail.part))
    this.root.addEventListener("builder:ready", () => this.subscribe(), { once: true })
    if (this.root.builder) this.subscribe()
  }

  disconnect() { this.unsubscribe?.() }

  get builder() { return this.root.builder }
  get store() { return this.builder.store }
  get doc() { return this.frameTarget.contentDocument }

  subscribe() {
    this.unsubscribe = this.store.subscribe((event) => this.apply(event))
  }

  setWidth(width) {
    this.frameTarget.style.width = width
  }

  loadPart(part) {
    const url = new URL(this.builder.urls.canvas, window.location.origin)
    url.searchParams.set("part", part)
    this.frameTarget.src = url.toString()
  }

  // --- canvas wiring ------------------------------------------------------

  bindCanvas() {
    const doc = this.doc
    if (!doc) return

    doc.addEventListener("click", (event) => this.onClick(event))
    doc.addEventListener("input", (event) => this.onInput(event))
    doc.addEventListener("keydown", (event) => this.onKeydown(event))
    // Links inside the canvas must not navigate while editing, and their
    // native drag would swallow the builder's own pointer events.
    doc.addEventListener("submit", (event) => event.preventDefault())
    doc.addEventListener("dragstart", (event) => event.preventDefault())
    doc.addEventListener("contextmenu", (event) => this.onContextMenu(event))

    this.markSelection()
    this.root.dispatchEvent(new CustomEvent("builder:canvas-ready", { bubbles: true }))
  }

  onClick(event) {
    const element = event.target.closest("[data-n]")
    if (!element) return
    if (element.tagName === "A") event.preventDefault()

    this.store.select(element.dataset.n)
  }

  onInput(event) {
    const element = event.target.closest("[contenteditable]")
    if (!element) return

    // The DOM is already correct; only the document needs to catch up.
    this.store.setProp(element.dataset.n, "html", element.innerHTML)
  }

  onKeydown(event) {
    // Clicking the canvas puts focus inside the iframe, so the editor's own
    // shortcuts would otherwise stop working exactly when they are wanted.
    this.builder.handleKeydown(event)

    if (event.target.isContentEditable) return
    if (!this.store.selectedId || this.store.selectedId === "root") return

    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault()
      return this.store.remove(this.store.selectedId)
    }

    // Arrows walk the siblings, which is far quicker than hunting for a
    // small element with the mouse.
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault()
      this.selectSibling(event.key === "ArrowUp" ? -1 : 1)
    }
  }

  selectSibling(direction) {
    const parent = this.store.parentOf(this.store.selectedId)
    if (!parent) return

    const index = this.store.indexOf(this.store.selectedId) + direction
    const sibling = parent.children[index]
    if (sibling) this.store.select(sibling.id)
  }

  // --- context menu -------------------------------------------------------

  onContextMenu(event) {
    const element = event.target.closest("[data-n]")
    if (!element || element.dataset.t === "page") return

    event.preventDefault()
    this.store.select(element.dataset.n)
    this.showMenu(element.dataset.n, event.clientX, event.clientY)
  }

  showMenu(id, x, y) {
    this.hideMenu()

    const labels = this.builder.schema.labels.ui
    const menu = this.doc.createElement("div")
    menu.className = "builder-context-menu"
    menu.style.left = `${x}px`
    menu.style.top = `${y}px`

    const items = [
      [labels.duplicate, () => this.builder.duplicate(id)],
      [labels.copy, () => this.builder.copy(id)],
      [labels.move_up, () => this.nudge(id, -1)],
      [labels.move_down, () => this.nudge(id, 1)],
      [labels.delete, () => this.store.remove(id)]
    ]

    items.forEach(([label, action]) => {
      const item = this.doc.createElement("button")
      item.type = "button"
      item.textContent = label
      item.addEventListener("click", () => { action(); this.hideMenu() })
      menu.append(item)
    })

    this.doc.body.append(menu)
    this.menu = menu
    this.doc.addEventListener("click", () => this.hideMenu(), { once: true })
  }

  hideMenu() {
    this.menu?.remove()
    this.menu = null
  }

  nudge(id, direction) {
    this.store.nudge(id, direction)
  }

  // --- store events -------------------------------------------------------

  apply(event) {
    switch (event.type) {
      case "style": return this.applyStyleEvent(event)
      case "select": return this.markSelection()
      case "structure": return this.applyStructure(event)
      case "document": return this.rerender()
      case "prop": return this.applyProp(event)
    }
  }

  // Named nodeElement, not element: a method called `element` would shadow
  // Stimulus's own `this.element` getter.
  nodeElement(id) {
    return this.doc?.querySelector(`[data-n="${CSS.escape(id)}"]`)
  }

  applyStyleEvent({ id, key, value, breakpoint }) {
    applyStyle(this.nodeElement(id), key, value, breakpoint, this.builder.schema.tokens.all)
  }

  // Props that are not live text need the node re-rendered; text is already
  // in the DOM because the user typed it there.
  applyProp({ id, key }) {
    if (key === "html") return
    this.rerenderNode(id)
  }

  applyStructure(event) {
    const { action, id } = event

    if (action === "remove") {
      this.nodeElement(id)?.remove()
      return
    }

    if (action === "move") {
      const element = this.nodeElement(id)
      const parent = this.nodeElement(event.parentId)
      if (!element || !parent) return this.rerender()
      const reference = parent.children[event.index] || null
      parent.insertBefore(element, reference)
      this.refreshEmptyMarkers()
      return
    }

    if (action === "insert") {
      const parent = this.nodeElement(event.parentId)
      if (!parent || !event.node.__html) return this.rerender()

      const template = this.doc.createElement("template")
      template.innerHTML = event.node.__html.trim()
      const inserted = template.content.firstElementChild
      parent.insertBefore(inserted, parent.children[event.index] || null)
      delete event.node.__html
      this.refreshEmptyMarkers()
      this.store.select(event.node.id)
    }
  }

  refreshEmptyMarkers() {
    this.doc?.querySelectorAll("[data-n]").forEach((element) => {
      const container = this.builder.schema.children[element.dataset.t]?.length
      if (!container) return
      if (element.children.length) element.removeAttribute("data-empty")
      else element.setAttribute("data-empty", "true")
    })
  }

  markSelection() {
    this.doc?.querySelectorAll("[data-selected]").forEach((el) => el.removeAttribute("data-selected"))
    if (this.store.selectedId) this.nodeElement(this.store.selectedId)?.setAttribute("data-selected", "true")
  }

  async rerenderNode(id) {
    const { ok, payload } = await BuilderApi.renderFragment(this.builder.urls.fragment, this.store.document)
    if (!ok) return

    const template = this.doc.createElement("template")
    template.innerHTML = payload.html.trim()
    const fresh = template.content.querySelector(`[data-n="${CSS.escape(id)}"]`)
    const current = this.nodeElement(id)
    if (fresh && current) current.replaceWith(fresh)
    this.markSelection()
  }

  async rerender() {
    const { ok, payload } = await BuilderApi.renderFragment(this.builder.urls.fragment, this.store.document)
    if (!ok) return

    this.doc.body.innerHTML = payload.html
    this.refreshEmptyMarkers()
    this.markSelection()
  }
}
